import { Component, inject, Input, signal } from '@angular/core';
import { ChatMessage } from '../model/search-message-dto';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { StatusConverterPipe } from '../../../../../common/pipes/status-converter.pipe';
import { OfferStatusPipe } from '../../../../../feature/offer/pipes/offer-status.pipe';
import { OrderStatusPipe } from '../../../../../feature/order/pipes/order-status.pipe';
import { DeliveryNoteStatusPipe } from '../../../../../feature/delivery-note/pipes/delivery-note-status.pipe';
import { InvoiceStatusPipe } from '../../../../../feature/invoice/pipes/invoice-status.pipe';
import { TranslatePipe } from '@ngx-translate/core';
import { DocType } from '../../../../../common/enum/document-type.enum';
import { ComponentBase } from '../../../../../common/component-base';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { UserService } from '../../../../../common/service/user.service';
import { Role } from '../../../../auth/role.enum';
import { ContractStatusPipe } from '../../../../../feature/contract/pipes/contract-status.pipe';
import { CustomerService } from '../../../../../common/service/customer.service';
import { SearchResultItem } from '../model/search-result-item';

import { LinkifyPipe } from '../../../../../common/pipes/linkify.pipe';

@Component({
  selector: 'gate-search-standard-result',
  standalone: true,
  imports: [TableModule, ButtonModule, StatusConverterPipe, TranslatePipe, DatePipe, CurrencyPipe, LinkifyPipe],
  templateUrl: './search-standard-result.component.html',
  styleUrl: './search-standard-result.component.scss',
  providers: [StatusConverterPipe, OfferStatusPipe, OrderStatusPipe, DeliveryNoteStatusPipe, InvoiceStatusPipe, ContractStatusPipe],

})
export class SearchStandardResultComponent extends ComponentBase {
  @Input() public searchMessage!: ChatMessage;
  private userService = inject(UserService);
  private customerService = inject(CustomerService);
  private userProfile = this.userService.getUserProfile();
  public activePdfItem = signal<SearchResultItem | null>(null);

  // Check if the user has the role of Account Manager (ACP User)
  isACPUser() {
    return this.userProfile.roles.includes(Role.AccountManager);
  }

  public get isAvailableDocumentType() {
    return this.searchMessage.searchResultItems?.some(x => x.documentType != null && x.documentType !== '');
  }
  public get isAvailableDocumentNo() {
    return this.searchMessage.searchResultItems?.some(x => x.documentNo != null && x.documentNo !== '');
  }
  public get isAvailableType() {
    return this.searchMessage.searchResultItems?.some(x => x.type != null && x.type !== '');
  }
  public get isAvailableName() {
    return this.searchMessage.searchResultItems?.some(x => x.name != null && x.name !== '');
  }
  public get isAvailableTitle() {
    return this.searchMessage.searchResultItems?.some(x => x.title != null && x.title !== '');
  }
  public get isAvailableEmail() {
    return this.searchMessage.searchResultItems?.some(x => x.email != null && x.email !== '');
  }
  public get isAvailablePhone() {
    return this.searchMessage.searchResultItems?.some(x => x.phone != null && x.phone !== '');
  }
  public get isAvailableMandant() {
    return this.searchMessage.searchResultItems?.some(x => x.mandant != null && x.mandant !== '');
  }
  public get isAvailableDescription() {
    return this.searchMessage.searchResultItems?.some(x => x.description != null && x.description !== '');
  }
  public get isAvailableDate() {
    return this.searchMessage.searchResultItems?.some(x => x.date != null);
  }
  public get isAvailableOrderer() {
    return this.searchMessage.searchResultItems?.some(x => x.orderer != null && x.orderer !== '');
  }
  public get isAvailableValueNet() {
    return this.searchMessage.searchResultItems?.some(x => x.valueNet != null);
  }
  public get isAvailableDocumentStatus() {
    return this.searchMessage.searchResultItems?.some(x => x.documentStatus != null);
  }

  public get extraColumns(): string[] {
    if (!this.searchMessage?.searchResultItems?.length) return [];
    const knownKeysLower = new Set([
      'companyno', 'documenttyp', 'documenttype', 'belegnummer', 'documentno', 'nettowert',
      'valuenet', 'bruttowert', 'valuegross', 'währung', 'documentcurrency', 'besteller',
      'orderer', 'datum', 'date', 'lieferadresse', 'deliveryaddress', 'invoiceaddress',
      'documenturl', 'documentstatus', 'beschreibung', 'description', 'position',
      'artikelbezeichnung', 'artikelbeschreibung', 'articledescription', 'articleline',
      'artikelnummer', 'articleno', 'unitprice', 'menge', 'quantity', 'microsofttenantid',
      'ownercompanyname', 'ownername', 'editionname', 'type', 'name', 'title',
      'email', 'phone', 'mandant', 'readonly', 'ispdfarchive'
    ]);
    const extraKeySet = new Set<string>();
    this.searchMessage.searchResultItems.forEach(item => {
      Object.keys(item).forEach(key => {
        if (!knownKeysLower.has(key.toLowerCase()) && item[key] !== null && item[key] !== undefined && item[key] !== '') {
          extraKeySet.add(key);
        }
      });
    });
    return Array.from(extraKeySet);
  }

  /**
   * Method to get the document URL based on the document type and document number.
   * @param document - The document number.
   * @param documentType - The document type enum or enum name as string.
   * @returns The document URL.
   */
  async openDocument(document: string, documentType: DocType | string, openPdf: boolean = false) {
    if (this.searchMessage.impersonateWith && this.isACPUser()) {
      let customerName: string | undefined;
      const customerId = this.searchMessage.impersonateWith;
      try {
        const customerData = await this.customerService.getCustomerData(customerId);
        customerName = customerData?.fullName;
      } catch (error) {
        console.error('Error fetching customer data for impersonation:', error);
      }

      try {
        await this.userService.startImpersonation({
          impersonatedUserSubject: null,
          customerId: customerId,
          impersonatedCustomerName: customerName,
          impersonatedType: 'Customer',
          initiatorType: 'ACP_User',
          log: true
        });
      } catch (error) {
        console.error('Error starting impersonation:', error);
      }
      this.navigateToDocument(document, documentType, openPdf);
    } else {
      this.navigateToDocument(document, documentType, openPdf);
    }
  }

  navigateToDocument(document: string, documentType: DocType | string, openPdf: boolean = false) {
    if (document && documentType) {
      const documentNumber = document?.replace('/', '_');
      const docTypeStr = typeof documentType == 'string' ? documentType.toLowerCase() : null;
      var url: string = '';
      const pdfQuery = openPdf ? '?openPdf=true' : '';
      if (documentType == DocType.Offer || docTypeStr == 'offer') url = `/offer/${documentNumber}${pdfQuery}`;
      else if (documentType == DocType.OrderConfirmation || docTypeStr == 'orderconfirmation') url = `/order/${documentNumber}${pdfQuery}`;
      else if (documentType == DocType.DeliveryNote || docTypeStr == 'deliverynote') url = `/delivery-note/${documentNumber}${pdfQuery}`;
      else if (documentType == DocType.Invoice || docTypeStr == 'invoice') url = `/invoice/${documentNumber}${pdfQuery}`;
      else if (documentType == DocType.ServiceContract || docTypeStr == 'servicecontract') url = `/contracts/${documentNumber}${pdfQuery}`;
      if (url) this.navigateToNewTab(url);
    }
  }

  /**
   * Method to get the document type string based on the document type enum.
   * @param documentType - The document type enum or enum name as string.
   * @returns The document type string.
   */
  public getDocumentTypeString(documentType: any): string {
    var type = '';
    const docTypeStr = typeof documentType == 'string' ? documentType?.toLowerCase() : null;
    if (documentType == DocType.Offer || docTypeStr == 'offer') type = 'menu.offers';
    else if (documentType == DocType.OrderConfirmation || docTypeStr == 'orderconfirmation') type = 'menu.orders';
    else if (documentType == DocType.DeliveryNote || docTypeStr == 'deliverynote') type = 'menu.deliveryNotes';
    else if (documentType == DocType.Invoice || docTypeStr == 'invoice') type = 'menu.invoices';
    else if (documentType == DocType.ServiceContract || docTypeStr == 'servicecontract') type = 'menu.contracts';
    return type ?? '';
  }

  public isValidDate(value: any): boolean {
    const regex = /^(?:\d{4}-\d{2}-\d{2}T(?:\d{2}:\d{2}:\d{2}|\d{6}-\d{3}Z)|[A-Za-z]{3} [A-Za-z]{3} \d{2} \d{4} \d{2}:\d{2}:\d{2} [A-Za-z]*[+-]?\d{4} \(.+\))$/;
    return regex.test(value);
  }

  public get isAvailableViewPdf() {
    return this.searchMessage.searchResultItems?.some(x => !x.readOnly && x.isPdfArchive && x.documentNo);
  }

  public openPdf(searchItem: SearchResultItem, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (!searchItem || !searchItem.documentNo || !searchItem.isPdfArchive || searchItem.readOnly) return;
    
    this.openDocument(searchItem.documentNo, searchItem.documentType, true);
  }
}
